import { asaFaq } from '@/pages/services/ai-security/content';
import { content as aiImprovementContent } from '@/pages/services/ai-improvement/page';
import { content as aiOperationContent } from '@/pages/services/ai-operation/page';
import { content as aiMarketingContent } from '@/pages/services/ai-marketing/page';
import { content as systemDevelopmentContent } from '@/pages/services/system-development/page';
import {
  SITE_NAME,
  SITE_NAME_EN,
  SITE_LOGO,
  SITE_ORIGIN,
  SITE_EMAIL,
  SITE_PHONE,
  absoluteUrl,
} from './site';
import { normalizePath, type RouteSeo } from './routes';
import type { SeoOverride } from './seoStore';

const ORG_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;

interface FaqItem {
  q: string;
  a: string;
}

const FAQ_BY_PATH: Record<string, FaqItem[]> = {
  '/services/ai-improvement': aiImprovementContent.faq ?? [],
  '/services/ai-operation': aiOperationContent.faq ?? [],
  '/services/ai-marketing': aiMarketingContent.faq ?? [],
  '/services/system-development': systemDevelopmentContent.faq ?? [],
  '/services/ai-security': asaFaq,
};

function buildOrganization() {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_NAME,
    alternateName: SITE_NAME_EN,
    url: SITE_ORIGIN,
    logo: SITE_LOGO,
    email: SITE_EMAIL,
    telephone: SITE_PHONE,
    foundingDate: '2025',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'JP',
      addressRegion: '兵庫県',
      addressLocality: '神戸市中央区',
      streetAddress: '磯辺通1丁目1番18号　カサベラ国際プラザビル707号室',
    },
  };
}

function buildBreadcrumb(name: string, pathname: string) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'ホーム',
        item: absoluteUrl('/'),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name,
        item: absoluteUrl(pathname),
      },
    ],
  };
}

export function buildSchema(
  route: RouteSeo | null,
  pathname: string,
  override: SeoOverride | null,
): object[] {
  if (!route || !route.indexable) return [];

  const canonical = absoluteUrl(pathname);
  const graph: object[] = [buildOrganization()];

  switch (route.schemaKind) {
    case 'home':
      graph.push({
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        url: SITE_ORIGIN,
        name: SITE_NAME,
        inLanguage: 'ja',
        publisher: { '@id': ORG_ID },
      });
      break;
    case 'service':
      graph.push({
        '@type': 'Service',
        name: route.serviceName || route.title,
        serviceType: route.serviceName || route.title,
        description: route.description,
        provider: { '@id': ORG_ID },
        areaServed: { '@type': 'Country', name: 'Japan' },
        url: canonical,
      });
      break;
    case 'contact':
      graph.push({
        '@type': 'ContactPage',
        name: route.title,
        description: route.description,
        url: canonical,
        inLanguage: 'ja',
      });
      break;
    case 'company':
      graph.push({
        '@type': 'AboutPage',
        name: route.title,
        description: route.description,
        url: canonical,
        inLanguage: 'ja',
      });
      break;
    case 'list':
      graph.push({
        '@type': 'CollectionPage',
        name: route.title,
        description: route.description,
        url: canonical,
        inLanguage: 'ja',
      });
      break;
    case 'article-news':
    case 'article-blog':
      if (override?.article) {
        graph.push({
          '@type': 'Article',
          headline: override.article.headline,
          description: override.article.description,
          image: override.article.image ? [override.article.image] : undefined,
          datePublished: override.article.datePublished,
          author: { '@id': ORG_ID },
          publisher: { '@id': ORG_ID },
          mainEntityOfPage: canonical,
          inLanguage: 'ja',
        });
      }
      break;
    default:
      break;
  }

  const faq = FAQ_BY_PATH[normalizePath(route.path)];
  if (faq && faq.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: faq.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a,
        },
      })),
    });
  }

  if (route.schemaKind !== 'home') {
    graph.push(buildBreadcrumb(route.title, pathname));
  }

  return graph;
}